import { useTranslation } from 'react-i18next'
import { Crosshair, Users, Lightbulb, DollarSign } from 'lucide-react'
import type { Positioning as PositioningData } from '@/lib/api/amazon-listing'
import { SectionCard } from './shared/SectionCard'

const USP_COLORS: Record<string, string> = {
  clear: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  vague: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  missing: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
}

const USP_LABELS: Record<string, string> = {
  clear: 'Klar', vague: 'Unklar', missing: 'Fehlt',
}

const PERCEPTION_LABELS: Record<string, string> = {
  budget: 'Budget', 'entry-level': 'Einstieg', 'mid-range': 'Mittelklasse',
  premium: 'Premium', luxury: 'Luxus', unclear: 'Unklar',
  low: 'Niedrig', medium: 'Mittel', high: 'Hoch',
}

interface PositioningProps {
  data: PositioningData
}

export function Positioning({ data }: PositioningProps) {
  const { t } = useTranslation()
  const r = (key: string) => t(`amazonListing.report.${key}`)

  return (
    <SectionCard title={r('positioning')}>
      <div className="space-y-5">
        <div className="border-l-4 border-orange-400 bg-orange-50 dark:bg-orange-900/10 p-4 rounded-r-lg">
          <p className="flex items-center gap-2 text-xs font-semibold text-neutral-500 dark:text-neutral-400 mb-1">
            <Crosshair className="h-3.5 w-3.5" /> {r('positioningStatement')}
          </p>
          <p className="text-sm italic text-neutral-800 dark:text-neutral-200">
            &ldquo;{data.positioning_statement}&rdquo;
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(['primary', 'secondary'] as const).map((level) => (
            <div key={level} className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-3">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-neutral-500 dark:text-neutral-400 mb-1">
                <Users className="h-3.5 w-3.5" /> {r(`${level}Audience`)}
              </p>
              <p className="text-sm text-neutral-800 dark:text-neutral-200">
                {data.target_audience[level]}
              </p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${USP_COLORS[data.usp_clarity] || USP_COLORS.vague}`}>
            {r('uspClarity')}: {USP_LABELS[data.usp_clarity] ?? data.usp_clarity}
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
            <DollarSign className="h-3 w-3" /> {r('pricePerception')}: {PERCEPTION_LABELS[data.price_perception?.toLowerCase()] ?? data.price_perception}
          </span>
        </div>

        <div>
          <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 mb-1">
            {r('competitiveAngle')}
          </p>
          <p className="text-sm text-neutral-700 dark:text-neutral-300">{data.competitive_angle}</p>
        </div>

        {data.guerilla_opportunities.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 mb-2">
              {r('guerillaOpportunities')}
            </p>
            <ul className="space-y-1.5">
              {data.guerilla_opportunities.map((opp, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                  <Lightbulb className="h-4 w-4 mt-0.5 shrink-0 text-yellow-500" />
                  {opp}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </SectionCard>
  )
}
